/**
 * ApproachA - 2クリック方式のセクション分類 UI
 *
 * PoC frontend/src/section-classifier/ApproachA.tsx から移植。
 * production 用に isSubmitting prop と必須 onConfirm を追加。
 *
 * 2 カラムレイアウト:
 * - 左: 発話一覧（クリックで始点/終点を設定）
 * - 右: セクションパネル（セクション選択 + ステータス表示）
 */

import { useState, useCallback, useMemo } from 'react';
import { UtteranceRow } from './components/UtteranceRow';
import { SectionPanel } from './components/SectionPanel';
import { ConfirmBar } from './components/ConfirmBar';
import type { SectionAssignment, SectionDefinition, TranscriptUtterance } from './types';

type ClickPhase = 'idle' | 'start' | 'end';

interface ApproachAProps {
  utterances: TranscriptUtterance[];
  sections: SectionDefinition[];
  onConfirm: (assignments: SectionAssignment[]) => void;
  isSubmitting?: boolean;
}

export function ApproachA({
  utterances,
  sections,
  onConfirm,
  isSubmitting = false,
}: ApproachAProps) {
  const [assignments, setAssignments] = useState<SectionAssignment[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [clickPhase, setClickPhase] = useState<ClickPhase>('idle');
  const [pendingStartIndex, setPendingStartIndex] = useState<number | null>(null);

  const utteranceAssignmentMap = useMemo(() => {
    const map: (SectionAssignment | null)[] = new Array(utterances.length).fill(null);
    for (const a of assignments) {
      for (let i = a.startUtteranceIndex; i <= a.endUtteranceIndex; i++) {
        map[i] = a;
      }
    }
    return map;
  }, [assignments, utterances.length]);

  const isOverlapping = useCallback(
    (startIdx: number, endIdx: number, excludeSectionId?: string) => {
      return assignments.some(a => {
        if (a.sectionId === excludeSectionId) return false;
        return startIdx <= a.endUtteranceIndex && endIdx >= a.startUtteranceIndex;
      });
    },
    [assignments],
  );

  const handleUtteranceClick = useCallback(
    (index: number) => {
      if (!activeSectionId || isSubmitting) return;

      if (clickPhase === 'start') {
        const existingAtIndex = utteranceAssignmentMap[index];
        if (existingAtIndex && existingAtIndex.sectionId !== activeSectionId) return;
        setPendingStartIndex(index);
        setClickPhase('end');
      } else if (clickPhase === 'end' && pendingStartIndex !== null) {
        if (index < pendingStartIndex) return;
        if (isOverlapping(pendingStartIndex, index, activeSectionId)) return;

        setAssignments(prev => {
          const filtered = prev.filter(a => a.sectionId !== activeSectionId);
          return [
            ...filtered,
            {
              sectionId: activeSectionId,
              startUtteranceIndex: pendingStartIndex,
              endUtteranceIndex: index,
            },
          ];
        });

        const currentSection = sections.find(s => s.id === activeSectionId);
        if (currentSection) {
          const nextSection = sections.find(
            s =>
              s.order > currentSection.order &&
              !assignments.some(a => a.sectionId === s.id),
          );
          if (nextSection) {
            setActiveSectionId(nextSection.id);
            setClickPhase('start');
          } else {
            setActiveSectionId(null);
            setClickPhase('idle');
          }
        }
        setPendingStartIndex(null);
      }
    },
    [
      activeSectionId,
      clickPhase,
      pendingStartIndex,
      utteranceAssignmentMap,
      isOverlapping,
      assignments,
      sections,
      isSubmitting,
    ],
  );

  const handleSectionSelect = useCallback(
    (sectionId: string) => {
      if (isSubmitting) return;
      if (activeSectionId === sectionId) {
        setActiveSectionId(null);
        setClickPhase('idle');
        setPendingStartIndex(null);
      } else {
        setAssignments(prev => prev.filter(a => a.sectionId !== sectionId));
        setActiveSectionId(sectionId);
        setClickPhase('start');
        setPendingStartIndex(null);
      }
    },
    [activeSectionId, isSubmitting],
  );

  const handleSectionRemove = useCallback((sectionId: string) => {
    setAssignments(prev => prev.filter(a => a.sectionId !== sectionId));
  }, []);

  const handleReset = useCallback(() => {
    setAssignments([]);
    setActiveSectionId(null);
    setClickPhase('idle');
    setPendingStartIndex(null);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(assignments);
  }, [assignments, onConfirm]);

  const guideMessage = useMemo(() => {
    if (!activeSectionId) return null;
    const section = sections.find(s => s.id === activeSectionId);
    if (!section) return null;
    if (clickPhase === 'start') {
      return {
        text: `「${section.name}」の開始位置をクリックしてください`,
        bgColor: section.bgColor,
        textColor: section.textColor,
      };
    }
    if (clickPhase === 'end') {
      return {
        text: `「${section.name}」の終了位置をクリックしてください`,
        bgColor: section.bgColor,
        textColor: section.textColor,
      };
    }
    return null;
  }, [activeSectionId, clickPhase, sections]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 overflow-y-auto bg-white">
          {guideMessage && (
            <div
              className={`sticky top-0 z-10 px-4 py-2 text-sm font-medium border-b ${guideMessage.bgColor} ${guideMessage.textColor}`}
            >
              {guideMessage.text}
            </div>
          )}

          {utterances.map((utterance, index) => {
            const assignment = utteranceAssignmentMap[index];
            const section = assignment
              ? sections.find(s => s.id === assignment.sectionId)
              : null;
            const isBlockStart = assignment && assignment.startUtteranceIndex === index;
            const isBlockEnd = assignment && assignment.endUtteranceIndex === index;
            const isPendingStart = pendingStartIndex === index;
            const isInPendingRange =
              clickPhase === 'end' && pendingStartIndex !== null && index >= pendingStartIndex;

            let clickable = false;
            if (clickPhase === 'start') {
              const existing = utteranceAssignmentMap[index];
              clickable = !existing || existing.sectionId === activeSectionId;
            } else if (clickPhase === 'end' && pendingStartIndex !== null) {
              clickable =
                index >= pendingStartIndex &&
                !isOverlapping(pendingStartIndex, index, activeSectionId ?? undefined);
            }

            const activeSection = activeSectionId
              ? sections.find(s => s.id === activeSectionId)
              : null;
            const bgColor = section
              ? section.bgColor
              : isInPendingRange && activeSection
                ? `${activeSection.bgColor} opacity-60`
                : undefined;
            const borderColor = section
              ? `border-l-4 ${section.borderColor}`
              : isInPendingRange && activeSection
                ? `border-l-4 ${activeSection.borderColor}`
                : 'border-l-4 border-l-transparent';

            return (
              <div key={utterance.id}>
                {isBlockStart && section && (
                  <div
                    className={`px-3 py-1.5 text-xs font-semibold ${section.bgColor} ${section.textColor} border-y border-gray-200 flex items-center gap-2`}
                  >
                    {section.name} 開始
                  </div>
                )}

                {isPendingStart && activeSection && (
                  <div
                    className={`px-3 py-1 text-xs font-semibold ${activeSection.bgColor} ${activeSection.textColor} border-y border-dashed border-current flex items-center gap-2`}
                  >
                    {activeSection.name} 開始位置（終了位置をクリック）
                  </div>
                )}

                <UtteranceRow
                  utterance={utterance}
                  index={index}
                  isHighlighted={isPendingStart}
                  sectionColor={bgColor}
                  borderColor={borderColor}
                  onClick={() => handleUtteranceClick(index)}
                  clickable={clickable}
                />

                {isBlockEnd && section && (
                  <div
                    className={`px-3 py-1 text-xs ${section.bgColor} ${section.textColor} border-b border-gray-200 flex items-center gap-2 opacity-70`}
                  >
                    {section.name} 終了
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="w-72 bg-gray-50 border-l border-gray-200 flex flex-col">
          <SectionPanel
            sections={sections}
            assignments={assignments}
            utterances={utterances}
            activeSectionId={activeSectionId}
            clickPhase={clickPhase}
            onSectionSelect={handleSectionSelect}
            onSectionRemove={handleSectionRemove}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>

      <ConfirmBar
        assignedCount={assignments.length}
        totalSections={sections.length}
        onConfirm={handleConfirm}
        onReset={handleReset}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
